import { useState, useEffect, useCallback } from "react";
import { Plus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fetchRelatedProducts, type ShopifyProduct } from "@/lib/shopify";
import { useCartStore, type CartItem } from "@/store/cartStore";
import { formatPrice } from "@/lib/utils";
import { VariantQuickPicker } from "@/components/VariantQuickPicker";

interface CartRecommendationsProps {
  cartItems: CartItem[];
  title?: string;
}

export const CartRecommendations = ({ cartItems, title = 'אולי תאהבו גם' }: CartRecommendationsProps) => {
  const [recommendations, setRecommendations] = useState<ShopifyProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ShopifyProduct | null>(null);
  const addItem = useCartStore((s) => s.addItem);

  // Cache key based on cart product handles
  const cacheKey = cartItems.map((i) => i.product.handle).sort().join(",");

  useEffect(() => {
    if (cartItems.length === 0) {
      setRecommendations([]);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const handles = cartItems.map((i) => i.product.handle);
        const excludeHandles = cartItems.map((i) => i.product.handle);
        const products = await fetchRelatedProducts(handles, excludeHandles, 8);
        if (!cancelled) setRecommendations(products);
      } catch (e) {
        console.error("Failed to load recommendations:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [cacheKey]);

  const handleQuickAdd = useCallback((product: ShopifyProduct) => {
    const variants = product.variants?.edges || [];
    const hasMultipleVariants = variants.length > 1;
    const hasOptions = (product.options || []).some(
      (o) => o.values.length > 1 && o.name !== "Title"
    );

    if (!hasMultipleVariants && !hasOptions && variants.length === 1) {
      // Direct add - single variant
      const variant = variants[0].node;
      addItem({
        product,
        variantId: variant.id,
        variantTitle: variant.title,
        price: variant.price,
        quantity: 1,
        selectedOptions: variant.selectedOptions,
        variantImage: variant.image || undefined,
      });
    } else {
      // Open variant picker
      setSelectedProduct(product);
    }
  }, [addItem]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (recommendations.length === 0) return null;

  return (
    <div className="relative flex flex-col h-full">
      <h3 className="sticky top-0 z-10 text-sm font-medium text-foreground bg-background pb-2 mb-1">{title}</h3>
      
      <div className="space-y-2 overflow-y-auto">
        {recommendations.map((product) => {
          const image = product.images?.edges?.[0]?.node;
          const price = product.priceRange?.minVariantPrice;

          return (
            <div
              key={product.id}
              className="flex items-center gap-3 p-2 border border-border/40 rounded-lg hover:border-primary/30 transition-colors"
            >
              {/* Image */}
              <div className="w-14 h-14 bg-muted rounded overflow-hidden flex-shrink-0">
                {image && (
                  <img
                    src={image.url}
                    alt={image.altText || product.title}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium leading-tight truncate text-foreground">
                  {product.title}
                </p>
                {price && (
                  <p className="text-xs text-primary font-semibold mt-0.5">
                    {formatPrice(price.amount, price.currencyCode)}
                  </p>
                )}
              </div>

              {/* Add button */}
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 flex-shrink-0 border-primary/30 hover:bg-primary hover:text-primary-foreground transition-colors"
                onClick={() => handleQuickAdd(product)}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          );
        })}
      </div>

      {/* Variant Picker Modal */}
      {selectedProduct && (
        <VariantQuickPicker
          product={selectedProduct}
          open={!!selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}
    </div>
  );
};
